import dropdowncss from "../css/dropdown.module.css";
import commoncss from "../css/common.module.css";
import { useEffect, useState, useRef } from "react";
import { ReactComponent as DownArrowIcon } from "../assets/icons/down_arrow.svg";
import { ReactComponent as UpArrowIcon } from "../assets/icons/up_arrow.svg";
import { ReqFieldLabel } from "../../myfin_v2/components/mis";

export default function Dropdown({
  req = false,
  class_ = "",
  class2_ = "",
  label,
  options = [],
  onSelect,
  placeHolder,
  selected = {},
  selValue = null,
  style2 = {},
}) {
  const [open, setOpen] = useState(false);
  const [selectedOptionLabel, setSelectedOptionLabel] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!open) {
      setSearchQuery("");
      setHighlightedIndex(-1);
    }
  }, [open]);

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [searchQuery]);

  useEffect(() => {
    if (highlightedIndex >= 0 && open && dropdownRef.current) {
      const menu = dropdownRef.current.querySelector('.' + dropdowncss.dropdown_menu);
      if (menu) {
        const activeItem = menu.children[highlightedIndex];
        if (activeItem) {
          activeItem.scrollIntoView({ block: 'nearest' });
        }
      }
    }
  }, [highlightedIndex, open]);

  useEffect(() => {
    if (options.length === 1) {
      const labelToSet = options[0].label;
      if (selectedOptionLabel !== labelToSet) {
        setSelectedOptionLabel(labelToSet);
        onSelect &&
          onSelect({ label: options[0].label, value: options[0].value });
      }
    } else if (selValue) {
      const matchedOption = options.find(
        (opt) =>
          String(opt.value).toLowerCase() === String(selValue).toLowerCase()
      );
      if (matchedOption) {
        // ✅ Always set — no guard — so it syncs whenever options or selValue changes
        setSelectedOptionLabel(matchedOption.label);
      } else {
        setSelectedOptionLabel(null);
      }
    } else if (selected && selected.label && selected.label !== selectedOptionLabel) {
      setSelectedOptionLabel(selected.label);
    } else if (!selValue && !selected?.label) {
      setSelectedOptionLabel(null);
    }
    // ✅ Only depends on external inputs, not internal state (selectedOptionLabel)
    // This prevents the stale closure bug that blocked re-renders
  }, [options, selected, selValue]);

  const handleSelect = (option) => {
    onSelect && onSelect({ label: option.label, value: option.value });
    setSelectedOptionLabel(option.label);
    setOpen(false);
  };

  return (
    <div
      ref={dropdownRef}
      className={`${dropdowncss.dropdown_wrapper} ${class_}`}
    >
      {label && (
        <span className={dropdowncss.dropdown_label}>
          {req && <ReqFieldLabel />} &nbsp; {label}
        </span>
      )}
      <div
        className={`${dropdowncss.dropdown} ${class_}`}
        onClick={() => setOpen(!open)}
      >
        <div
          className={`${class2_} ${dropdowncss.dropdown_selected} ${
            selectedOptionLabel ? dropdowncss.has_value : ""
          }`}
          style={style2}
          {...(selectedOptionLabel && selectedOptionLabel.trim() !== ""
            ? { "data-tooltip": selectedOptionLabel }
            : {})}
        >
          <input
            type="text"
            className={dropdowncss.dropdown_selected_text}
            style={{ border: 'none', background: 'transparent', outline: 'none', width: '100%', cursor: 'pointer', fontFamily: 'inherit', color: 'inherit' }}
            value={open ? searchQuery : (selectedOptionLabel || "")}
            placeholder={placeHolder}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              if (!open) setOpen(true);
            }}
            onClick={(e) => {
              if (open) e.stopPropagation();
            }}
            onKeyDown={(e) => {
              const filteredOptions = searchQuery 
                ? options.filter(opt => opt.label.toLowerCase().includes(searchQuery.toLowerCase())) 
                : options;

              if (!open) {
                if (e.key === "Enter" || e.key === "ArrowDown" || e.key === "ArrowUp") {
                  setOpen(true);
                }
                return;
              }

              if (e.key === "ArrowDown") {
                e.preventDefault();
                setHighlightedIndex(prev => prev < filteredOptions.length - 1 ? prev + 1 : prev);
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
              } else if (e.key === "Enter") {
                e.preventDefault();
                if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
                  handleSelect(filteredOptions[highlightedIndex]);
                }
              } else if (e.key === "Escape") {
                setOpen(false);
              }
            }}
            readOnly={!open}
          />
          <span className={dropdowncss.dropdown_arrow}>
            {open ? <UpArrowIcon /> : <DownArrowIcon />}
          </span>
        </div>

        <div
          className={`${dropdowncss.dropdown_menu} ${
            open ? dropdowncss.open : ""
          }`}
        >
          {(searchQuery ? options.filter(opt => opt.label.toLowerCase().includes(searchQuery.toLowerCase())) : options).map((opt, i) => (
            <div
              key={i}
              className={`${dropdowncss.dropdown_item} ${i === highlightedIndex ? dropdowncss.active : ""}`}
              onClick={() => handleSelect(opt)}
              onMouseEnter={() => setHighlightedIndex(i)}
            >
              {opt.label}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}